import { useCallback } from 'react';

import { useImportSrfMutation } from '../api/consignmentsApi';
import type { ConsignmentSummary, SrfImportParams } from './types';

export interface UseImportSrfOptions {
  onSuccess?: (data: ConsignmentSummary) => void;
  onError?: (error: unknown) => void;
}

/**
 * Wraps the raw `importSrf` mutation with unwrap + error handling, so callers
 * only supply `onSuccess` / `onError` and never touch RTK Query internals.
 * Mirrors `useCreateConsignment` / `useUpdateConsignment`.
 */
export const useImportSrf = (options?: UseImportSrfOptions) => {
  const [importSrf, result] = useImportSrfMutation();

  const handleImport = useCallback(
    async (params: SrfImportParams) => {
      try {
        const response = await importSrf(params).unwrap();
        options?.onSuccess?.(response);
        return response;
      } catch (error) {
        options?.onError?.(error);
        throw error;
      }
    },
    [importSrf, options]
  );

  return {
    importSrf: handleImport,
    isLoading: result.isLoading,
    isSuccess: result.isSuccess,
    isError: result.isError,
    error: result.error,
    data: result.data
  };
};
